import { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../design/dialog";
import { Input } from "../../../design/input";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  ErrorMessage,
} from "../../../design/fieldset";
import { Textarea } from "../../../design/textarea";
import { Select } from "../../../design/select";
import { useParticipant } from "../../../contexts/ParticipantContext";
import { HolidayOverride } from "../../../models/HolidayOverride";
import { HolidayRule } from "../../../models/HolidayRule";
import { useHolidaySchedule } from "../../../contexts/HolidayScheduleContext";
import moment from "moment";
import { Button } from "../../../design/button";
import { getParticipantIdForHolidayRule } from "../../../helpers/getParticipantIdForHolidayRule";
import { getHolidayDateRangeForRule } from "../../../helpers/getHolidayDateRangeForRule";
import DeleteHolidayOverrideButton from "../../DeleteHolidayOverrideButton";
import { Participant } from "../../../models/Participant";

interface Props {
  holidayRule: HolidayRule;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  year: number;
  existingOverride?: HolidayOverride;
}

const HolidayOverrideDialog: React.FC<Props> = ({
  holidayRule,
  isOpen,
  setIsOpen,
  year,
  existingOverride,
}) => {
  const { participants } = useParticipant();
  const { createHolidayOverride, updateHolidayOverride } = useHolidaySchedule();

  const [isSaving, setIsSaving] = useState(false);
  const [participantId, setParticipantId] = useState<
    Participant["_id"] | undefined
  >(existingOverride?.participantId);
  const [startTime, setStartTime] = useState<string>("");
  const [endTime, setEndTime] = useState<string>("");
  const [reason, setReason] = useState(existingOverride?.reason || "");
  const [errors, setErrors] = useState<{ participantId?: string }>({});

  const baseDateStr =
    holidayRule.date?.replace(/^\d{4}/, year.toString()) ??
    holidayRule.holiday?.dates.find((d) => d.startsWith(`${year}`));
  const baseDate = baseDateStr ? moment(baseDateStr) : null;

  useEffect(() => {
    if (!baseDate) return;

    if (existingOverride) {
      setParticipantId(existingOverride.participantId);
      const start = baseDate
        .clone()
        .add(existingOverride.startOffsetMinutes, "minutes")
        .format("YYYY-MM-DDTHH:mm");
      const end = baseDate
        .clone()
        .add(existingOverride.endOffsetMinutes, "minutes")
        .format("YYYY-MM-DDTHH:mm");
      setStartTime(start);
      setEndTime(end);
      setReason(existingOverride.reason || "");
    } else if (isOpen) {
      const defaultParticipantId = getParticipantIdForHolidayRule(
        holidayRule,
        year,
      );
      const { startDate, endDate } = getHolidayDateRangeForRule(
        holidayRule,
        year,
      );
      setParticipantId(defaultParticipantId);
      setStartTime(moment(startDate).format("YYYY-MM-DDTHH:mm"));
      setEndTime(moment(endDate).format("YYYY-MM-DDTHH:mm"));
    }
  }, [isOpen, existingOverride, holidayRule]);

  const handleSave = async () => {
    const newErrors: typeof errors = {};
    if (!participantId) newErrors.participantId = "participantId is required.";
    if (!startTime || !endTime) return;
    if (!baseDate) return;

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setIsSaving(true);
    try {
      const startOffsetMinutes = moment(startTime).diff(baseDate, "minutes");
      const endOffsetMinutes = moment(endTime).diff(baseDate, "minutes");

      if (existingOverride) {
        await updateHolidayOverride(
          holidayRule.holidaySchedule,
          holidayRule._id,
          existingOverride._id,
          {
            ...existingOverride,
            startOffsetMinutes,
            endOffsetMinutes,
            reason,
          },
        );
      } else {
        await createHolidayOverride(
          holidayRule.holidaySchedule,
          holidayRule._id,
          {
            participantId: participantId!,
            startOffsetMinutes,
            endOffsetMinutes,
            reason,
            holidayRule: holidayRule._id,
            year,
          },
        );
      }
      setIsOpen(false);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={setIsOpen}>
      <DialogTitle>
        {existingOverride
          ? `Edit Override for ${year}`
          : `Override Holiday Rule for ${year}`}
      </DialogTitle>
      <DialogBody>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Participant</Label>
              <Select
                value={participantId || ""}
                onChange={(e) => setParticipantId(e.target.value)}
              >
                <option value="">Select</option>
                {participants.map((p) => (
                  <option key={p._id} value={p._id}>
                    {p.name}
                  </option>
                ))}
              </Select>
              {errors.participantId && (
                <ErrorMessage>{errors.participantId}</ErrorMessage>
              )}
            </Field>
            <div className="flex gap-2">
              <Field className="w-1/2">
                <Label>Start Time</Label>
                <Input
                  type="datetime-local"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                />
              </Field>
              <Field className="w-1/2">
                <Label>End Time</Label>
                <Input
                  type="datetime-local"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                />
              </Field>
            </div>
            <Field>
              <Label>Reason</Label>
              <Textarea
                value={reason}
                placeholder="Optional reason for override"
                onChange={(e) => setReason(e.target.value)}
              />
            </Field>
          </FieldGroup>
        </Fieldset>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={() => setIsOpen(false)}>
          Cancel
        </Button>
        {existingOverride && (
          <DeleteHolidayOverrideButton
            holidayScheduleId={holidayRule.holidaySchedule}
            holidayRuleId={holidayRule._id}
            holidayOverrideId={existingOverride._id}
            onDelete={() => setIsOpen(false)}
          />
        )}
        <Button onClick={handleSave} disabled={isSaving}>
          {existingOverride ? "Update" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HolidayOverrideDialog;
